import { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  MessageCircle,
  Twitter,
  MapPin,
  Facebook,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Copy,
  Check,
  AlertTriangle,
  Info,
  DollarSign,
  Clock,
  Shield,
  ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/toast";
import { Link } from "react-router-dom";

// Platform icons and colors mapping
const platformConfig = {
  telegram: {
    icon: MessageCircle,
    color: '#00F0FF',
    bgColor: 'rgba(0, 240, 255, 0.1)',
    link: 'https://my.telegram.org'
  },
  twitter: {
    icon: Twitter,
    color: '#1DA1F2',
    bgColor: 'rgba(29, 161, 242, 0.1)',
    link: 'https://developer.twitter.com'
  },
  google_maps: {
    icon: MapPin,
    color: '#34A853',
    bgColor: 'rgba(52, 168, 83, 0.1)',
    link: 'https://console.cloud.google.com'
  },
  facebook: {
    icon: Facebook,
    color: '#1877F2',
    bgColor: 'rgba(24, 119, 242, 0.1)',
    link: 'https://developers.facebook.com'
  }
};

// Platform images (these remain static as they are external URLs)
const platformImages = {
  telegram: {
    step1: 'https://static.prod-images.emergentagent.com/jobs/776ffe23-b693-4cda-85e7-3d752af1720c/images/0bd75a1b0da983912e376f3a4e015a03486d50c617386f3f61e6caa9c46718f1.png',
    step2: 'https://static.prod-images.emergentagent.com/jobs/776ffe23-b693-4cda-85e7-3d752af1720c/images/630d07937ae417308b515303a87ea0e7b49e803a3ba5492ff2c50902815a6d6a.png',
    step3: 'https://static.prod-images.emergentagent.com/jobs/776ffe23-b693-4cda-85e7-3d752af1720c/images/630d07937ae417308b515303a87ea0e7b49e803a3ba5492ff2c50902815a6d6a.png',
    step4: 'https://static.prod-images.emergentagent.com/jobs/776ffe23-b693-4cda-85e7-3d752af1720c/images/8e920c819fb3c141106f74493f46bfe97667aa1698e37ecea4404fd8ade53bb0.png',
    step5: 'https://static.prod-images.emergentagent.com/jobs/776ffe23-b693-4cda-85e7-3d752af1720c/images/105d60a81f32cfe33b21fc244eca677f5cca74eca515bf1765f13b736af86c8b.png',
    step6: 'https://static.prod-images.emergentagent.com/jobs/776ffe23-b693-4cda-85e7-3d752af1720c/images/e114b8f849cc73bd9c983ee044836a8fd99e32071a3c19ebb8d4a6fa6a2d15a3.png',
    step7: 'https://static.prod-images.emergentagent.com/jobs/776ffe23-b693-4cda-85e7-3d752af1720c/images/e114b8f849cc73bd9c983ee044836a8fd99e32071a3c19ebb8d4a6fa6a2d15a3.png'
  },
  twitter: {
    step1: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/26abf5946d58e221a622283a0f6c71f8eb852d74dbdf3260a9c65340205fb746.png',
    step2: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/26abf5946d58e221a622283a0f6c71f8eb852d74dbdf3260a9c65340205fb746.png',
    step3: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/26abf5946d58e221a622283a0f6c71f8eb852d74dbdf3260a9c65340205fb746.png',
    step4: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/26abf5946d58e221a622283a0f6c71f8eb852d74dbdf3260a9c65340205fb746.png',
    step5: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/b7aca4fcfbce56eb2be10ce3f4aa11f79e98aaf2e3cfdb369d2396e6ae1433bb.png',
    step6: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/1e51243e9684c0dcb01c0e29fc1dcb86e3817bcb6ba92ed51d656a2f1a36cf66.png'
  },
  google_maps: {
    step1: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/08a03010e912606d4dd7b7eccb3e662f7168c614d77c00aa1c0b5df9e128fdf4.png',
    step2: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/6d8b15984514f5da4be7ddd8570e291d670f566507877883b191d9f190dd467d.png',
    step3: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/37ca98f0e89094a25790ac1951ba93fe4499f3a6403056b61c98fb8663db0e13.png',
    step4: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/8569ff28b19dd3d0dd81d4262071d030eaaf836794d8da1d8e3ad35dac047aa8.png',
    step5: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/fdc796cc840d7e6376c176ce79b868e8b237b4b995f365610f1efcb0deeaa006.png',
    step6: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/54bdb78c009cb5d3c1955cff0b8a7dca20bcedc548403feea385165137349c45.png',
    step7: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/df7518305fb2881b9159da5f22488d7d0f159f31e366e3a9d90d15a81ce0ad11.png'
  },
  facebook: {
    step1: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/51cf9ef207ba3e3938b9e5041621d841c973a972d2bf6aa9be4971efb04a2e1e.png',
    step2: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/51cf9ef207ba3e3938b9e5041621d841c973a972d2bf6aa9be4971efb04a2e1e.png',
    step3: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/51cf9ef207ba3e3938b9e5041621d841c973a972d2bf6aa9be4971efb04a2e1e.png',
    step4: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/51cf9ef207ba3e3938b9e5041621d841c973a972d2bf6aa9be4971efb04a2e1e.png',
    step5: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/51cf9ef207ba3e3938b9e5041621d841c973a972d2bf6aa9be4971efb04a2e1e.png',
    step6: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/51cf9ef207ba3e3938b9e5041621d841c973a972d2bf6aa9be4971efb04a2e1e.png',
    step7: 'https://static.prod-images.emergentagent.com/jobs/422ba00c-1e7d-4358-8784-6644faf70bf1/images/51cf9ef207ba3e3938b9e5041621d841c973a972d2bf6aa9be4971efb04a2e1e.png'
  }
};

export default function Guide() {
  const { t } = useTranslation();
  const [expandedGuide, setExpandedGuide] = useState(null);
  const [expandedStep, setExpandedStep] = useState({});
  const [copiedText, setCopiedText] = useState(null);

  const platformIds = ['telegram', 'twitter', 'google_maps', 'facebook'];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    toast.success(t('guidePage.copied'));
    setTimeout(() => setCopiedText(null), 2000);
  };

  const toggleStep = (guideId, stepIndex) => {
    const key = `${guideId}-${stepIndex}`;
    setExpandedStep(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getStepImage = (platformId, stepIndex) => {
    const images = platformImages[platformId];
    if (!images) return null;
    return images[`step${stepIndex + 1}`] || null;
  };

  return (
    <div className="space-y-8" data-testid="guide-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/platforms">
          <Button variant="outline" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('guidePage.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('guidePage.subtitle')}</p>
        </div>
      </div>

      {/* Platform Guides */}
      <div className="space-y-6">
        {platformIds.map((platformId) => {
          const config = platformConfig[platformId];
          const Icon = config.icon;
          const isExpanded = expandedGuide === platformId;
          const steps = t(`guidePage.platforms.${platformId}.steps`, { returnObjects: true }) || [];
          
          return (
            <div
              key={platformId}
              className="rounded-2xl overflow-hidden transition-all duration-300"
              style={{ 
                background: isExpanded ? config.bgColor : 'transparent',
                border: '4px solid #000000',
                boxShadow: '0 0 0 1px rgba(255,255,255,0.1)'
              }}
              data-testid={`guide-${platformId}`}
            >
              {/* Platform Header */}
              <button
                onClick={() => setExpandedGuide(isExpanded ? null : platformId)}
                className="w-full p-6 flex items-center justify-between text-start hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: config.bgColor }}
                  >
                    <Icon className="w-7 h-7" style={{ color: config.color }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">
                      {t(`guidePage.platforms.${platformId}.name`)}
                    </h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Shield className="w-4 h-4" />
                        {t(`guidePage.platforms.${platformId}.difficulty`)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {t(`guidePage.platforms.${platformId}.time`)}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {t(`guidePage.platforms.${platformId}.cost`)}
                      </span>
                    </div>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-6 h-6 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-muted-foreground" />
                )}
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="p-6 pt-0 space-y-6">
                  {/* Warning */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-yellow-500">{t('guidePage.warning')}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t(`guidePage.platforms.${platformId}.warning`)}
                      </p>
                    </div>
                  </div>

                  {/* Steps */}
                  <div className="space-y-4">
                    {Array.isArray(steps) && steps.map((step, stepIndex) => {
                      const stepKey = `${platformId}-${stepIndex}`;
                      const isStepExpanded = expandedStep[stepKey];
                      const stepImage = getStepImage(platformId, stepIndex);
                      const stepTips = step.tips || [];
                      
                      return (
                        <div
                          key={stepIndex}
                          className="rounded-xl border border-border overflow-hidden bg-card"
                        >
                          <button
                            onClick={() => toggleStep(platformId, stepIndex)}
                            className="w-full p-4 flex items-center gap-4 text-start hover:bg-muted/50 transition-colors"
                          >
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0"
                              style={{ backgroundColor: config.color }}
                            >
                              {stepIndex + 1}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-foreground">{step.title}</h4>
                            </div>
                            {isStepExpanded ? (
                              <ChevronUp className="w-5 h-5 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            )}
                          </button>

                          {isStepExpanded && (
                            <div className="p-4 pt-0 space-y-4">
                              {/* Description */}
                              <p className="text-muted-foreground whitespace-pre-line pe-14">
                                {step.description}
                              </p>

                              {/* Image */}
                              {stepImage && (
                                <div className="rounded-lg overflow-hidden border border-border">
                                  <img 
                                    src={stepImage} 
                                    alt={step.title}
                                    className="w-full h-auto"
                                    loading="lazy"
                                  />
                                </div>
                              )}

                              {/* Link */}
                              {stepIndex === 0 && config.link && (
                                <a
                                  href={config.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors"
                                  style={{ backgroundColor: config.color }}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  {t('guidePage.openLink')}
                                </a>
                              )}

                              {/* Tips */}
                              {Array.isArray(stepTips) && stepTips.length > 0 && (
                                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                                  <h5 className="font-bold text-sm flex items-center gap-2 text-primary mb-2">
                                    <Info className="w-4 h-4" />
                                    {t('guidePage.tips')}
                                  </h5>
                                  <ul className="text-sm text-muted-foreground space-y-1">
                                    {stepTips.map((tip, tipIndex) => (
                                      <li key={tipIndex} className="flex items-start gap-2">
                                        <span className="text-primary">•</span>
                                        {tip}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Back to Platforms Button */}
      <div className="flex justify-center pt-4">
        <Link to="/platforms">
          <Button variant="outline" size="lg">
            <ArrowLeft className="w-5 h-5 me-2" />
            {t('guidePage.backToPlatforms')}
          </Button>
        </Link>
      </div>
    </div>
  );
}
